import { CheckCircle, ChevronRight, Cpu, Database, Lock, Radio, Server, Shield, Users } from "lucide-react";
import {
  architectureFeatures,
  complianceFrameworks,
  privacyFeatures,
  serverImg,
  aiProFeatures,
  aiEnterpriseFeatures,
  aiProviderOptions,
} from "../../data/platformContent";

export function PlatformSecuritySection() {
  return (
    <section className="pb-24" id="security">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div
              className="rounded-2xl border border-teal-500/15 overflow-hidden"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="p-8 pb-0">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
                    <Shield size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="text-slate-100 text-lg" style={{ fontWeight: 700 }}>
                      Trust Architecture
                    </div>
                    <div className="text-on-dark-muted text-sm">Privacy-by-Design Framework</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  {complianceFrameworks.map((fw) => (
                    <div
                      key={fw.name}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl border border-teal-500/15 bg-teal-500/5"
                    >
                      <CheckCircle size={16} className="text-teal-400" />
                      <span className="text-teal-300 text-sm" style={{ fontWeight: 700 }}>
                        {fw.name}
                      </span>
                      <span className="text-on-dark-muted text-xs text-center">{fw.region}</span>
                    </div>
                  ))}
                </div>
                <p className="text-on-dark-muted text-xs mt-3 leading-relaxed">
                  Framework labels indicate control mapping, not that DeskReplay holds a regime-specific certificate.
                </p>
              </div>

              <div className="relative h-48 overflow-hidden">
                <img src={serverImg} alt="Self-hosted infrastructure" className="w-full h-full object-cover opacity-30" />
                <div
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(to top, var(--color-slate-900) 0%, transparent 60%)" }}
                />
                <div className="absolute bottom-4 left-8 right-8">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-teal-500/30" />
                    <span className="text-teal-400 text-xs uppercase tracking-widest" style={{ fontWeight: 600 }}>
                      Enterprise self-hosted
                    </span>
                    <div className="flex-1 h-px bg-teal-500/30" />
                  </div>
                  <div className="text-on-dark-soft text-xs text-center mt-2 leading-relaxed">
                    Recordings stay on storage you operate in your VPC. SaaS plans use DeskReplay-operated infrastructure.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-teal-400 text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
              The Non-Invasive Standard
            </p>
            <h2
              className="text-slate-100 mb-6"
              style={{ fontSize: "2rem", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.2" }}
            >
              Built for CISOs.
              <br />
              <span className="text-teal-400">Trusted by Legal.</span>
            </h2>
            <p className="text-on-dark-muted leading-relaxed mb-8">
              DeskReplay&apos;s architecture was reviewed against GDPR Article 25 (Data Protection by Design), HIPAA&apos;s
              Minimum Necessary Standard, and NIST Cybersecurity Framework before a single line of production code was
              written.
            </p>

            <div className="flex flex-col gap-4">
              {[
                "Screen-output capture by default; optional session audio on Pro and Enterprise",
                "No keylogging, clipboard capture, or password harvesting",
                "Granular retention policies: set auto-deletion windows per endpoint group",
                "Role-based access control: only authorized investigators can view recordings",
                "Consent workflow support: employee notice and consent management built-in",
                "Per-segment SHA-256 when segments finalize; verify files against the database",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CISO-facing: transport standards + what we actually persist */}
        <div className="mt-20 pt-16 border-t border-slate-800/50">
          <p className="text-teal-400 text-sm uppercase tracking-widest mb-3" style={{ fontWeight: 600 }}>
            Transport & evidence controls
          </p>
          <h2
            className="text-slate-100 mb-4 max-w-3xl"
            style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.85rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.25" }}
          >
            Encrypted live viewing, standard crypto profiles, and where your data lives
          </h2>
          <p className="text-on-dark-muted max-w-3xl leading-relaxed mb-12">
            DeskReplay does not rely on a proprietary tunnel for the investigator browser. Live viewing uses industry-standard
            real-time streaming with encryption in transit: low latency for authorized subscribers, encoded screen data from
            agents, and delivery under protections your security team already reviews for browser-based media, not a custom
            obfuscation layer marketed as security.
          </p>

          <div className="grid lg:grid-cols-2 gap-8 mb-10">
            <div
              className="rounded-2xl border border-teal-500/15 p-8"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-teal-600/30 flex items-center justify-center">
                  <Radio size={20} className="text-teal-400" />
                </div>
                <h3 className="text-slate-100 text-base" style={{ fontWeight: 700 }}>
                  Live console path (browser)
                </h3>
              </div>
              <ul className="space-y-3 text-on-dark-muted text-sm leading-relaxed">
                <li>
                  Encrypted paths from agent to your server and from server to the investigator browser, with standard TLS and
                  media protections on the live leg.
                </li>
                <li>
                  This matches what security teams already review for enterprise browser streaming, rather than a one-off codec
                  pipe.
                </li>
              </ul>
            </div>

            <div
              className="rounded-2xl border border-teal-500/15 p-8"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-emerald-600/30 flex items-center justify-center">
                  <Database size={20} className="text-emerald-400" />
                </div>
                <h3 className="text-slate-100 text-base" style={{ fontWeight: 700 }}>
                  What we store &amp; how
                </h3>
              </div>
              <ul className="space-y-3 text-on-dark-muted text-sm leading-relaxed">
                <li>
                  <span className="text-slate-300">Metadata</span> in your deployed database: organizations,
                  clients, endpoints, recording sessions, segment rows (timestamps, sizes, optional SHA-256 per finalized MP4
                  segment).
                </li>
                <li>
                  <span className="text-slate-300">Artifacts</span>: MP4 segments on disk under your configured recording root
                  (tenant-scoped paths), or optional object storage (e.g. S3-compatible / R2) referenced from segment rows, so
                  retention, encryption at rest, and backups stay{" "}
                  <span className="text-slate-300">your policy and keys</span>.
                </li>
                <li>
                  <span className="text-slate-300">Console</span>: investigators see sessions and playback through your
                  hosted admin app; access is gated by your IdP/RBAC, not ours.
                </li>
              </ul>
            </div>
          </div>

          <div
            className="rounded-2xl border border-slate-700/80 p-8 lg:p-10"
            style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-slate-800/80 flex items-center justify-center shrink-0">
                <Lock size={20} className="text-lime-300" />
              </div>
              <div>
                <h3 className="text-slate-100 text-base mb-3" style={{ fontWeight: 700 }}>
                  &quot;Forensic-grade&quot; in plain language
                </h3>
                <p className="text-on-dark-muted text-sm leading-relaxed mb-4">
                  No ISO badge defines &quot;forensic-grade live capture.&quot; DeskReplay is built for{" "}
                  <span className="text-slate-300">evidence-oriented operations</span>: self-hosted data, screen-output capture,
                  optional segment hashing, administrative audit events in your database, and
                  standard encrypted transport for live viewing. Suitability for legal or regulatory proceedings still depends on
                  your processes, jurisdictional rules, and how you run chain-of-custody. We give technical controls you can
                  map to NIST, SOC 2, or internal policy; we do not replace your counsel or lab certification.
                </p>
                <p className="text-on-dark-muted text-xs leading-relaxed border-t border-slate-700/60 pt-4">
                  <span className="text-slate-400">Agent · Server · Console:</span> the Windows agent captures and encodes;
                  your server coordinates signaling, recording, and storage; the browser console is the investigator UI. All
                  three sit <span className="text-slate-400">inside your environment</span> when you self-host.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PlatformPrivacySection() {
  return (
    <section
      className="py-24 border-t border-slate-800/50"
      id="privacy"
      style={{ background: "linear-gradient(180deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-emerald-400 text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
            Trust & Privacy-by-Design
          </p>
          <h2
            className="text-slate-100"
            style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
          >
            The Compliance Softer Pitch
          </h2>
          <p className="text-on-dark-muted mt-4 text-base max-w-xl mx-auto">
            Four foundational commitments aimed at common DPO, CISO, and Legal priorities at the same time.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {privacyFeatures.map((feature) => (
            <div
              key={feature.title}
              className="rounded-2xl border border-teal-500/15 p-8 hover:border-teal-500/25 transition-all duration-300 group"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-12 h-12 rounded-xl ${feature.iconBg} flex items-center justify-center shadow-lg`}>
                  <feature.icon size={22} className="text-white" />
                </div>
                <div>
                  <h3 className="text-slate-100 text-base" style={{ fontWeight: 700 }}>
                    {feature.title}
                  </h3>
                  <p className="text-on-dark-muted text-xs" style={{ fontWeight: 500 }}>
                    {feature.subtitle}
                  </p>
                </div>
              </div>
              <p className="text-on-dark-muted text-sm leading-relaxed mb-5">{feature.description}</p>
              <div className="flex flex-wrap gap-2">
                {feature.badges.map((b) => (
                  <span
                    key={b}
                    className="px-3 py-1 rounded-full border border-teal-500/20 bg-teal-500/5 text-teal-300 text-xs"
                    style={{ fontWeight: 500 }}
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PlatformArchitectureFeaturesSection() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div id="architecture" className="scroll-mt-28 text-center mb-16">
        <p className="text-teal-400 text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
          Platform Architecture
        </p>
        <h2
          className="text-slate-100"
          style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
        >
          For the CTO & IT Architecture Team
        </h2>
        <p className="text-on-dark-muted mt-4 text-base max-w-xl mx-auto">
          Enterprise-grade infrastructure designed for seamless deployment into existing security and identity frameworks.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
        {architectureFeatures.map((feature) => (
          <div
            key={feature.title}
            id={feature.title === "Enterprise Identity" ? "identity" : undefined}
            className={`rounded-xl border p-6 hover:scale-[1.02] transition-all duration-300 ${feature.border}`}
            style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
          >
            <div className={`w-11 h-11 rounded-xl ${feature.bg} flex items-center justify-center mb-4`}>
              <feature.icon size={20} className={feature.color} />
            </div>
            <h3 className="text-slate-100 mb-2 text-base" style={{ fontWeight: 700 }}>
              {feature.title}
            </h3>
            <p className="text-on-dark-muted text-sm leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PlatformDeploymentDiagramSection() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        id="deployment"
        className="scroll-mt-28 rounded-2xl border border-teal-500/15 p-8 lg:p-12"
        style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
      >
        <div className="text-center mb-10">
          <h3 className="text-slate-100 text-xl" style={{ fontWeight: 700 }}>
            Deployment Architecture
          </h3>
          <p className="text-on-dark-muted text-sm mt-2">Everything runs in your environment</p>
        </div>
        <div className="grid grid-cols-3 gap-4 items-center max-w-3xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 rounded-xl bg-teal-600/20 border border-teal-500/30 flex items-center justify-center mx-auto mb-3">
              <Cpu size={28} className="text-teal-400" />
            </div>
            <div className="text-slate-200 text-sm mb-1" style={{ fontWeight: 600 }}>
              Windows Agent
            </div>
            <div className="text-on-dark-muted text-xs">WinExe / WinService</div>
            <div className="text-on-dark-muted text-xs mt-1">Managed Endpoints</div>
          </div>

          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1 w-full">
              <div className="flex-1 h-px bg-green-400/35" />
              <ChevronRight size={14} className="text-green-300" />
            </div>
            <span className="text-green-300 text-xs">Encrypted stream / TLS</span>
            <div className="flex items-center gap-1 w-full">
              <div className="flex-1 h-px bg-green-400/35" />
              <ChevronRight size={14} className="text-green-300" />
            </div>
            <span className="text-green-300 text-xs">Encrypted</span>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
              <Server size={28} className="text-emerald-400" />
            </div>
            <div className="text-slate-200 text-sm mb-1" style={{ fontWeight: 600 }}>
              DeskReplay Server
            </div>
            <div className="text-on-dark-muted text-xs">Your VPC · Self-hosted</div>
            <div className="text-on-dark-muted text-xs mt-1">AI Processing</div>
          </div>

          <div />
          <div className="flex flex-col items-center gap-1 mt-4">
            <ChevronRight size={14} className="text-green-300 rotate-90" />
            <div className="h-8 w-px bg-green-400/30" />
            <span className="text-green-300 text-xs">RBAC Auth</span>
          </div>
          <div />

          <div />
          <div className="text-center mt-2">
            <div className="w-16 h-16 rounded-xl bg-teal-600/20 border border-teal-500/30 flex items-center justify-center mx-auto mb-3">
              <Users size={28} className="text-teal-400" />
            </div>
            <div className="text-slate-200 text-sm mb-1" style={{ fontWeight: 600 }}>
              Investigator Console
            </div>
            <div className="text-on-dark-muted text-xs">OIDC · bring your own IdP</div>
            <div className="text-on-dark-muted text-xs mt-1">AI Query Interface</div>
          </div>
          <div />
        </div>
      </div>
    </div>
  );
}

export function PlatformAIIntelligenceSection() {
  return (
    <section
      className="py-24 border-t border-slate-800/50"
      id="ai-intelligence"
      style={{ background: "linear-gradient(180deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-violet-400 text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
            AI Intelligence Layer
          </p>
          <h2
            className="text-slate-100"
            style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
          >
            Answers in seconds, not hours.
          </h2>
          <p className="text-on-dark-muted mt-4 text-base max-w-2xl mx-auto">
            Search every word ever visible on screen, ask questions in plain English, and let enterprise AI surface
            compliance signals across your entire recording archive.
          </p>
        </div>

        {/* Pro plan features */}
        <div className="mb-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-slate-800/60" />
            <span
              className="px-3 py-1 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-xs uppercase tracking-widest"
              style={{ fontWeight: 600 }}
            >
              Pro Plan
            </span>
            <div className="flex-1 h-px bg-slate-800/60" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {aiProFeatures.map((feature) => (
              <div
                key={feature.title}
                className="rounded-2xl border border-violet-500/15 p-8 hover:border-violet-500/25 transition-all duration-300 group"
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-12 h-12 rounded-xl ${feature.iconBg} flex items-center justify-center shadow-lg shadow-violet-500/20`}>
                    <feature.icon size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-slate-100 text-base" style={{ fontWeight: 700 }}>
                      {feature.title}
                    </h3>
                    <p className="text-on-dark-muted text-xs" style={{ fontWeight: 500 }}>
                      {feature.subtitle}
                    </p>
                  </div>
                </div>
                <p className="text-on-dark-muted text-sm leading-relaxed mb-5">{feature.description}</p>
                <div className="flex flex-wrap gap-2">
                  {feature.badges.map((b) => (
                    <span
                      key={b}
                      className="px-3 py-1 rounded-full border border-violet-500/20 bg-violet-500/5 text-violet-300 text-xs"
                      style={{ fontWeight: 500 }}
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enterprise plan features */}
        <div className="mt-14">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-slate-800/60" />
            <span
              className="px-3 py-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-xs uppercase tracking-widest"
              style={{ fontWeight: 600 }}
            >
              Enterprise Plan
            </span>
            <div className="flex-1 h-px bg-slate-800/60" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {aiEnterpriseFeatures.map((feature) => (
              <div
                key={feature.title}
                className={`rounded-xl border p-6 hover:scale-[1.02] transition-all duration-300 ${feature.border}`}
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className={`w-11 h-11 rounded-xl ${feature.bg} flex items-center justify-center mb-4`}>
                  <feature.icon size={20} className={feature.color} />
                </div>
                <h3 className="text-slate-100 mb-2 text-base" style={{ fontWeight: 700 }}>
                  {feature.title}
                </h3>
                <p className="text-on-dark-muted text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* AI provider callout */}
        <div
          className="mt-14 rounded-2xl border border-violet-500/15 p-8 lg:p-10"
          style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
        >
          <div className="mb-8 text-center">
            <h3 className="text-slate-100 text-xl mb-2" style={{ fontWeight: 700 }}>
              Your data. Your AI provider.
            </h3>
            <p className="text-on-dark-muted text-sm max-w-2xl mx-auto leading-relaxed">
              AI processing is configurable to your infrastructure. Connect OpenAI, Gemini, Claude, DeepSeek, Grok,
              Azure OpenAI, or Ollama with your own credentials, or run models entirely on-premises when residency
              requires it.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {aiProviderOptions.map((provider) => (
              <div
                key={provider.name}
                className="rounded-xl border border-slate-700/60 p-5"
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <h4 className="text-slate-200 text-sm" style={{ fontWeight: 700 }}>
                      {provider.name}
                    </h4>
                    <p className="text-violet-400/90 text-xs mt-0.5">{provider.label}</p>
                  </div>
                  <span
                    className="shrink-0 px-2 py-0.5 rounded-full border border-violet-500/20 bg-violet-500/5 text-violet-400 text-xs"
                    style={{ fontWeight: 500 }}
                  >
                    {provider.tier}
                  </span>
                </div>
                <p className="text-on-dark-muted text-xs leading-relaxed">{provider.description}</p>
              </div>
            ))}
          </div>

          <p className="text-on-dark-muted text-xs text-center mt-6 leading-relaxed">
            AI features process OCR text and metadata - not raw video. Recordings stay on your configured storage;
            only extracted text is sent to your chosen AI provider.
          </p>
        </div>
      </div>
    </section>
  );
}

export function PlatformEnterpriseIdentityFocusSection() {
  const identity = architectureFeatures.find((f) => f.title === "Enterprise Identity");
  if (!identity) return null;
  return (
    <section className="py-16 border-t border-slate-800/50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-teal-400 text-sm uppercase tracking-widest mb-4 text-center" style={{ fontWeight: 600 }}>
          Enterprise Identity
        </p>
        <h2
          className="text-slate-100 text-center mb-6"
          style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
        >
          SSO and RBAC on <span className="text-teal-400">your IdP</span>
        </h2>
        <p className="text-on-dark-muted text-center mb-10 leading-relaxed">
          Investigators and admins authenticate through the same OIDC providers you already operate, with no parallel credential
          stores. MFA, conditional access, and lifecycle policies stay where security already owns them.
        </p>
        <div
          className={`rounded-2xl border p-8 ${identity.border}`}
          style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
        >
          <div className={`w-14 h-14 rounded-xl ${identity.bg} flex items-center justify-center mb-6`}>
            <identity.icon size={26} className={identity.color} />
          </div>
          <h3 className="text-slate-100 text-lg mb-3" style={{ fontWeight: 700 }}>
            {identity.title}
          </h3>
          <p className="text-on-dark-muted text-sm leading-relaxed">{identity.description}</p>
        </div>
      </div>
    </section>
  );
}
